import React from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  SELECTION_CHANGE_COMMAND,
  FORMAT_TEXT_COMMAND,
  FORMAT_ELEMENT_COMMAND,
  $getSelection,
  $isNodeSelection,
  $isRangeSelection,
  $createParagraphNode,
  COMMAND_PRIORITY_NORMAL,
  KEY_MODIFIER_COMMAND,
  $isElementNode,
  $getRoot,
} from 'lexical';
import { $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link';
import { $setBlocksType } from '@lexical/selection';
import { $getNearestNodeOfType, mergeRegister, $findMatchingParent } from '@lexical/utils';
import {
  $isListNode,
  ListNode,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
} from '@lexical/list';
import {
  $createHeadingNode,
  $createQuoteNode,
  $isHeadingNode,
  $isQuoteNode,
} from '@lexical/rich-text';
import clsx from 'clsx';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { $isCollapsibleTitleNode } from '../nodes/CollapsibleTitleNode';
import { $isCollapsibleContentNode } from '../nodes/CollapsibleContentNode';
import { $isImageNode } from '../nodes/ImageNode';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@dashboard/components/ui/dropdown-menu';
import TypeBoldIcon from '../icons/TypeBoldIcon';
import TypeItalicIcon from '../icons/TypeItalicIcon';
import TypeStrikethroughIcon from '../icons/TypeStrikethroughIcon';
import TypeUnderlineIcon from '../icons/TypeUnderlineIcon';
import LinkIcon from '../icons/LinkIcon';
import TextCenterIcon from '../icons/TextCenterIcon';
import TextLeftIcon from '../icons/TextLeftIcon';
import TextRightIcon from '../icons/TextRightIcon';
import TypeH1Icon from '../icons/TypeH1Icon';
import TypeH2Icon from '../icons/TypeH2Icon';
import TypeH3Icon from '../icons/TypeH3Icon';
import HeadingIcon from '../icons/HeadingIcon';
import { getSelectedNode } from '../utils/getSelectedNode';
import { sanitizeUrl } from '../utils/url';
import ListOrderedIcon from '../icons/ListOrderedIcon';
import ListUnorderedIcon from '../icons/ListUnorderedIcon';
import Tooltip from '@dashboard/components/Tooltip';
import TextQuoteIcon from '../icons/TextQuoteIcon';
import useFileQueries from '@/dashboard/hooks/queries/useFileQueries';
import { useAuth } from '@/dashboard/context/AuthContext/AuthContext';
import getUrlFromCdn, { generateSrcSetUrls } from '@dashboard/services/getUrlFromCdn';
import { INSERT_IMAGE_COMMAND } from './ImagePlugin';
import { logException } from '@codemirror/view';
import shortUuid from '../../shortUuid';
import preloadImages from '../../preloadImages';

const LowPriority = 1;

const supportedHeadingTypes = new Set(['h1', 'h2', 'h3']);

function Divider() {
  return (
    <div
      className="w-4 h-4 bg-gray-200 dark:bg-accent flex-shrink-0 flex-grow-0 flex-1"
      style={{ flexBasis: '1px' }}
    />
  );
}

const BLOCK_TYPE_LABELS = {
  h1: 'Heading 1',
  h2: 'Heading 2',
  h3: 'Heading 3',
  ul: 'Bullet list',
  ol: 'Numbered list',
  quote: 'Quote',
  paragraph: 'Normal text',
};

const BLOCK_TYPE_ICONS = {
  h1: TypeH1Icon,
  h2: TypeH2Icon,
  h3: TypeH3Icon,
  ul: ListUnorderedIcon,
  ol: ListOrderedIcon,
  quote: TextQuoteIcon,
  paragraph: HeadingIcon,
};

const ALIGNMENT_LABELS = { left: 'Left align', center: 'Center align', right: 'Right align' };

function getBlockTypeIconLabel(blockType) {
  switch (blockType) {
    case 'h1':
      return (
        <>
          <TypeH1Icon /> Heading 1
        </>
      );
    case 'h2':
      return (
        <>
          <TypeH2Icon /> Heading 2
        </>
      );
    case 'h3':
      return (
        <>
          <TypeH3Icon /> Heading 3
        </>
      );
    case 'ul':
      return (
        <>
          <ListUnorderedIcon /> Bullet list
        </>
      );
    case 'ol':
      return (
        <>
          <ListOrderedIcon /> Numbered list
        </>
      );
    case 'quote':
      return (
        <>
          <TextQuoteIcon /> Quote
        </>
      );
    case 'paragraph':
    default:
      return (
        <>
          <HeadingIcon /> Normal text
        </>
      );
  }
}

function BlockTypeDropdownList({ editor, blockType }) {
  const formatParagraph = () => {
    editor.update(() => {
      const selection = $getSelection();

      if ($isRangeSelection(selection)) {
        $setBlocksType(selection, () => $createParagraphNode());
      }
    });
  };

  const formatHeading = newHeadingType => {
    // If the heading type is different than the current block type, then
    // we want to convert the current block to a heading
    if (newHeadingType !== blockType) {
      editor.update(() => {
        const selection = $getSelection();

        if ($isRangeSelection(selection)) {
          $setBlocksType(selection, () => $createHeadingNode(newHeadingType));
        }
      });
    } else {
      // If the heading type is the same as the current block type, then
      // we want to convert the heading to a paragraph
      formatParagraph();
    }
  };

  const formatQuote = () => {
    editor.update(() => {
      const selection = $getSelection();

      if ($isRangeSelection(selection)) {
        $setBlocksType(selection, () => $createQuoteNode());
      }
    });
  };

  const formatBulletList = () => {
    editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
  };

  const formatNumberedList = () => {
    editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
  };

  return (
    <DropdownMenuContent
      className="shadow rounded overflow-hidden border border-gray-50 dark:border-border bg-white dark:bg-popover flex flex-col gap-y-1 text-sm min-w-32 mt-2"
      align="start"
      onCloseAutoFocus={e => {
        e.preventDefault();
        editor.focus();
      }}
    >
      <DropdownMenuItem
        className={clsx(
          'hover:bg-gray-50 dark:hover:bg-muted hover:text-black dark:hover:text-foreground px-2 py-1 cursor-pointer outline-none whitespace-nowrap space-x-1 align-middle',
          {
            'background-gray-50 text-black dark:text-foreground': blockType === 'paragraph',
          },
        )}
        onClick={formatParagraph}
        type="button"
      >
        <span className="inline-flex gap-x-1.5 items-center">
          {getBlockTypeIconLabel('paragraph')}
        </span>
      </DropdownMenuItem>

      <DropdownMenuItem
        className={clsx(
          'hover:bg-gray-50 dark:hover:bg-muted hover:text-black dark:hover:text-foreground px-2 py-1 cursor-pointer outline-none whitespace-nowrap space-x-1 align-middle',
          {
            'background-gray-50 text-black dark:text-foreground': blockType === 'h1',
          },
        )}
        onClick={() => formatHeading('h1')}
        type="button"
      >
        <span className="inline-flex gap-x-1.5 items-center">{getBlockTypeIconLabel('h1')}</span>
      </DropdownMenuItem>
      <DropdownMenuItem
        className={clsx(
          'hover:bg-gray-50 dark:hover:bg-muted hover:text-black dark:hover:text-foreground px-2 py-1 cursor-pointer outline-none whitespace-nowrap space-x-1 align-middle',
          {
            'background-gray-50 text-black dark:text-foreground': blockType === 'h2',
          },
        )}
        onClick={() => formatHeading('h2')}
        type="button"
      >
        <span className="inline-flex gap-x-1.5 items-center">{getBlockTypeIconLabel('h2')}</span>
      </DropdownMenuItem>
      <DropdownMenuItem
        className={clsx(
          'hover:bg-gray-50 dark:hover:bg-muted hover:text-black dark:hover:text-foreground px-2 py-1 cursor-pointer outline-none whitespace-nowrap space-x-1 align-middle',
          {
            'background-gray-50 text-black dark:text-foreground': blockType === 'h3',
          },
        )}
        onClick={() => formatHeading('h3')}
        type="button"
      >
        <span className="inline-flex gap-x-1.5 items-center">{getBlockTypeIconLabel('h3')}</span>
      </DropdownMenuItem>

      <DropdownMenuItem
        className={clsx(
          'hover:bg-gray-50 dark:hover:bg-muted hover:text-black dark:hover:text-foreground px-2 py-1 cursor-pointer outline-none whitespace-nowrap space-x-1 align-middle',
          {
            'background-gray-50 text-black dark:text-foreground': blockType === 'ul',
          },
        )}
        onClick={formatBulletList}
        type="button"
      >
        <span className="inline-flex gap-x-1.5 items-center">{getBlockTypeIconLabel('ul')}</span>
      </DropdownMenuItem>

      <DropdownMenuItem
        className={clsx(
          'hover:bg-gray-50 dark:hover:bg-muted hover:text-black dark:hover:text-foreground px-2 py-1 cursor-pointer outline-none whitespace-nowrap space-x-1 align-middle',
          {
            'background-gray-50 text-black dark:text-foreground': blockType === 'ol',
          },
        )}
        onClick={formatNumberedList}
        type="button"
      >
        <span className="inline-flex gap-x-1.5 items-center">{getBlockTypeIconLabel('ol')}</span>
      </DropdownMenuItem>

      <DropdownMenuItem
        className={clsx(
          'hover:bg-gray-50 dark:hover:bg-muted hover:text-black dark:hover:text-foreground px-2 py-1 cursor-pointer outline-none whitespace-nowrap space-x-1 align-middle',
          {
            'background-gray-50 text-black dark:text-foreground': blockType === 'quote',
          },
        )}
        onClick={formatQuote}
        type="button"
      >
        <span className="inline-flex gap-x-1.5 items-center">{getBlockTypeIconLabel('quote')}</span>
      </DropdownMenuItem>
    </DropdownMenuContent>
  );
}

function AlignmentOptionsDropdownList({ editor }) {
  return (
    <DropdownMenuContent
      className="shadow rounded overflow-hidden border border-gray-50 dark:border-border bg-white dark:bg-popover flex flex-col gap-y-1 text-xs min-w-32 mt-2"
      align="start"
      onCloseAutoFocus={e => {
        e.preventDefault();
        editor.focus();
      }}
    >
      <DropdownMenuItem
        className={clsx(
          'hover:bg-gray-50 dark:hover:bg-muted hover:text-black dark:hover:text-foreground px-2 py-1 cursor-pointer outline-none whitespace-nowrap space-x-1 align-middle',
        )}
        onClick={() => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'left')}
        type="button"
      >
        <span className="flex items-center gap-x-1.5">
          <TextLeftIcon /> Left align
        </span>
      </DropdownMenuItem>

      <DropdownMenuItem
        className={clsx(
          'hover:bg-gray-50 dark:hover:bg-muted hover:text-black dark:hover:text-foreground px-2 py-1 cursor-pointer outline-none whitespace-nowrap space-x-1 align-middle',
        )}
        onClick={() => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'center')}
        type="button"
      >
        <span className="flex items-center gap-x-1.5">
          <TextCenterIcon /> Center align
        </span>
      </DropdownMenuItem>
      <DropdownMenuItem
        className={clsx(
          'hover:bg-gray-50 dark:hover:bg-muted hover:text-black dark:hover:text-foreground px-2 py-1 cursor-pointer outline-none whitespace-nowrap space-x-1 align-middle',
        )}
        onClick={() => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'right')}
        type="button"
      >
        <span className="flex items-center gap-x-1.5">
          <TextRightIcon /> Right align
        </span>
      </DropdownMenuItem>
    </DropdownMenuContent>
  );
}

const isHeadingType = type => supportedHeadingTypes.has(type);

export default function ToolbarPlugin({ setIsLinkEditMode, setIsLoading }) {
  const [editor] = useLexicalComposerContext();
  const toolbarRef = useRef(null);
  const [blockType, setBlockType] = useState('paragraph');
  const [, setHeadingType] = useState('h1');
  const isLink = false;
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isStrikethrough, setIsStrikethrough] = useState(false);
  const [, setIsQuote] = useState(false);
  const [, setIsNumberedList] = useState(false);
  const [, setIsBulletList] = useState(false);
  const [elementFormat, setElementFormat] = useState('left');
  const { uploadFile } = useFileQueries();
  const { sellerId } = useAuth();

  const updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if ($isNodeSelection(selection)) {
      const image = selection.getNodes().find($isImageNode);
      if (image) {
        setElementFormat(image.getAlignment());
      }
      return;
    }
    if ($isRangeSelection(selection)) {
      const anchorNode = selection.anchor.getNode();
      let element;
      if (anchorNode.getKey() === 'root') {
        element = anchorNode;
      } else {
        // Find the block element. When the cursor is inside a collapsible's
        // title or content, the top-level element is the collapsible container,
        // which hides the actual block (e.g. h2) from the toolbar. Walk up to
        // the nearest block whose parent is the root or a collapsible
        // title/content wrapper instead.
        element =
          $findMatchingParent(anchorNode, node => {
            if (!$isElementNode(node) || node.isInline()) return false;
            const parent = node.getParent();
            if (parent === null) return false;
            return (
              parent.getKey() === 'root' ||
              $isCollapsibleTitleNode(parent) ||
              $isCollapsibleContentNode(parent)
            );
          }) || anchorNode.getTopLevelElementOrThrow();
      }
      let resolvedBlockType = 'paragraph';
      if ($isListNode(element)) {
        const parentList = $getNearestNodeOfType(anchorNode, ListNode);
        resolvedBlockType = parentList ? parentList.getTag() : element.getTag();
      } else {
        resolvedBlockType = $isHeadingNode(element) ? element.getTag() : element.getType();
      }
      setBlockType(resolvedBlockType);
      setHeadingType(isHeadingType(resolvedBlockType) ? resolvedBlockType : null);

      // Update text format
      setIsBold(selection.hasFormat('bold'));
      setIsItalic(selection.hasFormat('italic'));
      setIsUnderline(selection.hasFormat('underline'));
      setIsStrikethrough(selection.hasFormat('strikethrough'));
      setIsQuote($isQuoteNode(element));
      setIsNumberedList(resolvedBlockType === 'ol');
      setIsBulletList(resolvedBlockType === 'ul');

      // Update links
      const node = getSelectedNode(selection);
      const parent = node.getParent();

      let matchingParent;
      if ($isLinkNode(parent)) {
        // If node is a link, we need to fetch the parent paragraph node to set format
        matchingParent = $findMatchingParent(
          node,
          parentNode => $isElementNode(parentNode) && !parentNode.isInline(),
        );
      }

      // If matchingParent is a valid node, pass it's format type
      setElementFormat(
        $isElementNode(matchingParent)
          ? matchingParent.getFormatType()
          : $isElementNode(node)
            ? node.getFormatType()
            : parent?.getFormatType() || 'left',
      );
    }
  }, [editor]);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          updateToolbar();
        });
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          updateToolbar();
          return false;
        },
        LowPriority,
      ),
    );
  }, [editor, updateToolbar]);

  useEffect(() => {
    return editor.registerCommand(
      KEY_MODIFIER_COMMAND,
      payload => {
        const event = payload;
        const { code, ctrlKey, metaKey } = event;

        if (code === 'KeyK' && (ctrlKey || metaKey)) {
          event.preventDefault();
          let url;
          if (!isLink) {
            setIsLinkEditMode(true);
            url = sanitizeUrl('https://');
          } else {
            setIsLinkEditMode(false);
            url = null;
          }

          return editor.dispatchCommand(TOGGLE_LINK_COMMAND, {
            url,
            target: '_blank',
            rel: 'noopener noreferrer',
          });
        }
        return false;
      },
      COMMAND_PRIORITY_NORMAL,
    );
  }, [editor, isLink, setIsLinkEditMode]);

  const insertLink = useCallback(() => {
    if (!isLink) {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, {
        url: sanitizeUrl('https://'),
        target: '_blank',
        rel: 'noopener noreferrer',
      });
    } else {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
    }
  }, [editor, isLink]);

  function createHiddenFileInput() {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/jpeg, image/png, image/jpg, image/gif, image/webp, image/svg+xml';
    input.multiple = false;
    input.style.display = 'none';
    input.id = 'lexical-image-input';
    document.body.appendChild(input);

    return input;
  }

  let input = null;
  const loadImage = event => {
    const reader = new FileReader();
    let file;
    let fileType;

    reader.onload = function (e) {
      const img = new Image(); // Create an image object

      img.onload = async function () {
        setIsLoading(true);
        // Now the image object has the file data loaded
        const width = parseInt(img.width > 448 ? 448 : img.width);

        // Calculate the aspect ratio and the resulting height
        const aspectRatio = img.width / img.height;
        const newHeight = parseInt(width / aspectRatio);

        // Create a canvas element to resize the image
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = newHeight;

        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, newHeight);

        const tmpId = shortUuid();
        const base64Image = canvas.toDataURL(fileType);

        editor.dispatchCommand(INSERT_IMAGE_COMMAND, {
          payload: {
            src: base64Image,
            isLoading: true,
            width: width,
            height: newHeight,
            id: tmpId,
          },
        });

        try {
          const { data } = await uploadFile({
            file,
            bucket: 'images',
            sellerId,
          });

          const imageUrl = getUrlFromCdn(data.location);
          const urls = generateSrcSetUrls({ url: imageUrl, width, height: newHeight });
          preloadImages({ urls: Object.values(urls) }).catch(err => console.error(err));

          editor.dispatchCommand(INSERT_IMAGE_COMMAND, {
            payload: {
              src: imageUrl,
              isLoading: false,
              width: width,
              height: newHeight,
            },
            nodeId: tmpId,
          });
        } catch (err) {
          logException(err);
          editor.update(() => {
            $getRoot()
              .getChildren()
              .forEach(node => {
                if (node.__id === tmpId) {
                  node.remove();
                }
              });
          });
        }

        setIsLoading(false);

        // Remove value from file input to allow same file to be selected again
        input.value = '';
      };

      img.src = e.target.result; // Set the image source to the result from FileReader

      return '';
    };

    if (event.target.files !== null) {
      file = event.target.files[0];
      fileType = file.type;
      reader.readAsDataURL(file);
    }
  };

  useEffect(() => {
    input = createHiddenFileInput();

    // listen for file selection
    input.addEventListener('change', loadImage);

    return () => {
      input.removeEventListener('change', loadImage);
      input.remove();
    };
  }, []);

  return (
    <div
      className="flex flex-row items-center justify-start bg-white dark:bg-background relative gap-x-1 w-full overflow-auto mb-2"
      ref={toolbarRef}
    >
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <ToolbarButton label={BLOCK_TYPE_LABELS[blockType] || 'Normal text'} isDropdown>
            {React.createElement(BLOCK_TYPE_ICONS[blockType] || HeadingIcon)}
            <ChevronDownIcon className="w-3 h-3" />
          </ToolbarButton>
        </DropdownMenuTrigger>

        <BlockTypeDropdownList editor={editor} blockType={blockType} />
      </DropdownMenu>

      <Divider />

      <ToolbarButton
        label="Bold"
        isActive={isBold}
        onClick={() => {
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold');
        }}
      >
        <TypeBoldIcon />
      </ToolbarButton>

      <ToolbarButton
        label="Italic"
        isActive={isItalic}
        onClick={() => {
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic');
        }}
      >
        <TypeItalicIcon />
      </ToolbarButton>

      <ToolbarButton
        label="Underline"
        isActive={isUnderline}
        onClick={() => {
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline');
        }}
      >
        <TypeUnderlineIcon />
      </ToolbarButton>

      <ToolbarButton
        label="Strikethrough"
        isActive={isStrikethrough}
        onClick={() => {
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough');
        }}
      >
        <TypeStrikethroughIcon />
      </ToolbarButton>

      <Divider />

      <ToolbarButton label="Link" isActive={isLink} onClick={insertLink}>
        <LinkIcon />
      </ToolbarButton>

      <Divider />

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <ToolbarButton label={ALIGNMENT_LABELS[elementFormat] || 'Left align'} isDropdown>
            {elementFormat === 'center' && <TextCenterIcon />}
            {elementFormat === 'right' && <TextRightIcon />}
            {elementFormat !== 'center' && elementFormat !== 'right' && <TextLeftIcon />}
            <ChevronDownIcon className="w-3 h-3" />
          </ToolbarButton>
        </DropdownMenuTrigger>

        <AlignmentOptionsDropdownList editor={editor} />
      </DropdownMenu>
    </div>
  );
}

export const toolbarItemClass =
  'border-none flex bg-none p-1.5 rounded cursor-pointer align-middle hover:bg-gray-100 dark:hover:bg-muted focus:bg-gray-100 dark:focus:bg-muted outline-none';

// Forwards props and ref so a dropdown trigger can use it as its own button rather than wrap it in one.
const ToolbarButton = React.forwardRef(function ToolbarButton(
  { label = '', onClick = () => null, isActive = false, isDropdown = false, children, ...props },
  ref,
) {
  const toolbarDropdownItemClass = `inline-flex items-center gap-x-1.5 text-xs leading-tight whitespace-nowrap`;
  const toolbarItemActiveClass = `bg-gray-100 dark:bg-muted`;

  return (
    <Tooltip label={label} asChild>
      <button
        ref={ref}
        type="button"
        onClick={onClick}
        aria-label={label}
        {...props}
        className={clsx({
          [toolbarItemClass]: true,
          [toolbarItemActiveClass]: isActive,
          [toolbarDropdownItemClass]: isDropdown,
        })}
      >
        {children}
      </button>
    </Tooltip>
  );
});
